import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface PredictionRow {
  readonly predictionId: string;
  readonly predictionType: string;
  readonly entityType: string;
  readonly predictedValue: string | null;
  readonly confidenceScore: string;
  readonly riskLevel: string | null;
  readonly isLowConfidence: boolean;
  readonly status: string;
}

const PREDICTION_TYPES = [
  '', 'JOB_COMPLETION', 'APPROVAL_DELAY', 'BUDGET_CONSUMPTION', 'PAYMENT_DELAY',
  'CAMPAIGN_COMPLETION', 'VENDOR_UTILIZATION', 'RESOURCE_REQUIREMENT', 'SLA_BREACH',
  'VENDOR_PERFORMANCE', 'PAINTER_PERFORMANCE', 'FORECAST', 'CLASSIFICATION',
];

/**
 * M25 — Prediction Center (§9).
 * Lists generated predictions with their explainable confidence and risk band.
 * Low-confidence outputs are flagged so a human reviewer never mistakes an
 * uncertain prediction for a firm one (BR-06). AI is advisory only (BR-01).
 */
export function M25PredictionCenterPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PredictionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [predictionType, setPredictionType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = predictionType ? `?predictionType=${encodeURIComponent(predictionType)}` : '';
    apiRequest<{ data: PredictionRow[]; total: number }>(`/v1/ai/predictions${query}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, predictionType]);

  return (
    <main aria-label="prediction-center">
      <h1>{tr('m25.prediction.title')}</h1>
      <label htmlFor="prediction-type-filter">{tr('m25.prediction.filterLabel')}</label>
      <select id="prediction-type-filter" value={predictionType} onChange={(e) => setPredictionType(e.target.value)}>
        {PREDICTION_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m25.prediction.allTypes')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m25.prediction.loading')}</p> : (
        <>
          <p aria-label="prediction-count">{tr('m25.prediction.count', { count: total })}</p>
          <table aria-label="prediction-table">
            <thead>
              <tr><th>{tr('m25.prediction.colType')}</th><th>{tr('m25.prediction.colEntity')}</th><th>{tr('m25.prediction.colPredicted')}</th><th>{tr('m25.prediction.colConfidence')}</th><th>{tr('m25.prediction.colRisk')}</th><th>{tr('m25.prediction.colFlag')}</th><th>{tr('m25.prediction.colStatus')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.predictionId}>
                  <td>{r.predictionType}</td>
                  <td>{r.entityType}</td>
                  <td>{r.predictedValue ?? '—'}</td>
                  <td>{decimal(r.confidenceScore)}</td>
                  <td>{r.riskLevel ?? '—'}</td>
                  <td>{r.isLowConfidence ? tr('m25.prediction.lowConfidence') : tr('m25.prediction.ok')}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
